
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsx mdx */



const layoutProps = {
  
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <h2>{`Existing CSS`}</h2>
    <p>{`There are a couple of implementation details that you should be aware of, if you choose to use
styled-components together with existing CSS.`}</p>
    <p>{`styled-components generates an actual stylesheet with classes, and attaches those classes to
the DOM nodes of styled components via the `}<inlineCode parentName="p">{`className`}</inlineCode>{` prop.
It injects the generated stylesheet at the end of the head of the document during runtime.`}</p>
    <h3>{`Styling normal React components`}</h3>
    <p>{`If you use the `}<inlineCode parentName="p">{`styled(MyComponent)`}</inlineCode>{` notation and `}<inlineCode parentName="p">{`MyComponent`}</inlineCode>{` does not
render the passed-in `}<inlineCode parentName="p">{`className`}</inlineCode>{` prop, then no styles will be applied.
To avoid this issue, make sure your component attaches the passed-in className to a DOM node:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`class MyComponent extends React.Component {
  render() {
    // Attach the passed-in className to the DOM node
    return <div className={this.props.className} />
  }
}
`}</code></pre>
    <p>{`If you have pre-existing styles with a class, you can combine the global class with the
passed-in one:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`class MyComponent extends React.Component {
  render() {
    // Attach the passed-in className to the DOM node
    return <div className={\`some-global-class \${this.props.className}\`} />
  }
}
`}</code></pre>
    <h3>{`Issues with specificity`}</h3>
    <p>{`If you apply a global class together with a styled component class, the result might not be
what you're expecting. If a property is defined in both classes with the same specificity,
the last one will win.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`// MyComponent.js
const MyComponent = styled.div\`background-color: green;\`;

// my-component.css
.red-bg {
  background-color: red;
}

// For some reason this component still has a green background,
// even though you're trying to override it with the "red-bg" class!
<MyComponent className="red-bg" />
`}</code></pre>
    <p>{`In the above example the styled component class takes precedence over the global class, since
styled-components injects its styles during runtime at the end of the `}<inlineCode parentName="p">{`<head>`}</inlineCode>{` by default.
Thus its styles win over other single classname selectors.`}</p>
    <p>{`One solution is to bump up the specificity of the selectors in your stylesheet:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-css"
      }}>{`/* my-component.css */
.red-bg.red-bg {
  background-color: red;
}
`}</code></pre>
    <h3>{`Avoiding conflicts with third-party styles and scripts`}</h3>
    <p>{`If you deploy styled-components on a page you don't fully control, you may need to take
precautions to ensure that your component styles don't conflict with those of the host page.`}</p>
    <p>{`The most common problem is insufficient specificity. For example, consider a host page with this
style rule:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-css"
      }}>{`body.my-body button {
  padding: 24px;
}
`}</code></pre>
    <p>{`Since the rule contains a classname and two tag names, it has higher specificity than the single
classname selector generated by this styled component:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`styled.button\`
  padding: 16px;
\`
`}</code></pre>
    <p>{`There's no way to give your components complete immunity from the host page's styles, but you can
at least boost the specificity of their style rules with
`}<a parentName="p" {...{
        "href": "https://github.com/QuickBase/babel-plugin-styled-components-css-namespace"
      }}>{`babel-plugin-styled-components-css-namespace`}</a>{`,
which allows you to specify a CSS namespace for all of your styled components. A good namespace
would be something like `}<inlineCode parentName="p">{`#my-widget`}</inlineCode>{`, if all of your styled-components render in a container
with `}<inlineCode parentName="p">{`id="my-widget"`}</inlineCode>{`, since ID selectors have more specificity than any number of classnames.`}</p>
    <p>{`A rarer problem is conflicts between two instances of styled-components on the page. You can avoid
this by defining `}<inlineCode parentName="p">{`process.env.SC_ATTR`}</inlineCode>{` in the code bundle with your styled-components instance.
This value overrides the default `}<inlineCode parentName="p">{`<style>`}</inlineCode>{` tag attribute, `}<inlineCode parentName="p">{`data-styled`}</inlineCode>{` (`}<inlineCode parentName="p">{`data-styled-components`}</inlineCode>{` in v3 and lower), allowing
each styled-components instance to recognize its own tags.`}</p>
    </MDXLayout>;
}

;
MDXContent.isMDXComponent = true;